import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import { ContactUsMode } from '../../components/forms/ContactForm';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiScrollingLogo } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import ScrollingLogosWidget from '../../widgets/animated/ScrollingLogosWidget';
import ScrollingTextWidget from '../../widgets/animated/ScrollingTextWidget';
import ContactUsWidget from '../../widgets/interactive/ContactUsWidget';
import UseCasesShowcase from '../../widgets/showcases/UseCasesShowcase';

type UseCasesPageStaticProps = {
  scrollingLogos: StrapiApiScrollingLogo[];
};

export const getStaticProps: GetStaticProps<UseCasesPageStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const props: UseCasesPageStaticProps = { scrollingLogos };
  return withGlobalDefaultStaticProps(context, { props });
};

const UseCasesPage: NextPageWithLayout<UseCasesPageStaticProps> = (props) => {
  const { scrollingLogos } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.UseCases);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Use Cases"
        title="Transform support across the enterprise"
        description="Offer best-in-class support to all your teams thanks to our Generative AI platform. Instantly and automatically address multiple use cases."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrow={true}>
        <ScrollingTextWidget texts={['ITSM', 'Self-service', 'HR helpdesk', 'Employee Onboarding', 'Identity access management']} />
      </PageSection>
      <PageSection>
        <UseCasesShowcase
          orientation="left"
          title="Self service"
          description="Say goodbye to support-related slowdowns: rely on the only self-service support platform your employees will love. With our generative AI solution on Slack, MS Teams and Jira SM support portal, employees have only one place to go for assistance: a secure interface to automatically and instantly solve issues, find answers, perform tasks and access information with no need to wait."
          tags={[
            'Task automation such as group management, software access',
            'Instant answers to How-to questions, IT troubleshooting',
            'Approvals automation for calendar, groups, emails etc.',
            'AI knowledge management for instant human-like info',
            'Business insights to IT Admins to avoid road blocks',
          ]}
          imageUrl={CDNAssets.getUrl('use-cases-page-self-service')}
        />
        <UseCasesShowcase
          orientation="right"
          title="HR helpdesk"
          description="Accelerate HR assistance with Gaspar AI. Offer personalized HR support 24/7, provide instant answers to HR questions, automate tasks, speed up time-off approvals and free up your HR team to focus on making an impact on employee's work life."
          tags={[
            'Task automation such as time-off requests',
            'Instant answers to HR related questions',
            'Personalized info about project deadlines and PTO balance',
            'Approvals automation for time-off requests',
          ]}
          imageUrl={CDNAssets.getUrl('use-cases-page-hr-helpdesk')}
        />
        <UseCasesShowcase
          orientation="left"
          title="IT Service Management"
          description="Enhance your ITSM with AI to maximize ROI, support employees instantly and free up your IT helpdesk team. Resolve issues on your company's chat platform with no human intervention, facilitate hand-off for complex issues, categorize tickets automatically, enhance your knowledge base with much-needed content."
          tags={[
            'Automated employee issue resolution',
            'Knowledge base instant info access',
            'Ticket automation: creation, categorization and syncing',
            'Performance metrics for IT Admins',
            'Knowledge base article suggestions',
            'Proactive insights for common employee issues',
          ]}
          imageUrl={CDNAssets.getUrl('use-cases-page-it-service-management')}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="More use cases" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-identity-access-management'),
              orientation: 'left',
              title: 'Identity access management',
              description:
                'Resetting passwords, giving access to groups and email lists or removing users from channels should be as easy and quick as sending a message. Gaspar AI automates account and access management issues and boosts efficiency and security.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-employee-support-jira'),
              orientation: 'right',
              title: 'Employee support on Jira Software',
              description:
                "The ONLY Generative AI helpdesk platform that works with Jira Software. If you haven't upgraded yet to JSM, you can still leverage our AI helpdesk. Automate ticket creation and promptly resolve issues to boost productivity, enhance efficiency, and delight your team.",
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-employee-onboarding'),
              orientation: 'left',
              title: 'Employee onboarding',
              description:
                "AI-powered, automated onboarding that ensures fast new hires' integration. Easily create new accounts, connect with HR and give new employees the access they need on day one.",
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-employee-offboarding'),
              orientation: 'right',
              title: 'Employee offboarding',
              description:
                "When people leave, ensure you quickly revoke access to accounts, systems, folders, apps. This improves your company's security and minimizes vulnerabilities.",
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-jira-data-center'),
              orientation: 'left',
              title: 'AI helpdesk on Jira Data Center',
              description:
                'The only chatbot that works directly on both JSM cloud and Data Center.  Seamless integration for improved and automated employee support with no switching costs.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-private-secure-support'),
              orientation: 'right',
              title: 'Private & secure employee support ',
              description:
                "If you want to integrate AI into your support operations but are conscious about security, Gaspar AI can run on your company's cloud infrastructure. No need to worry about your data being shared with any cloud systems, you can use your own.",
            },
          ]}
        />
      </PageSection>
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection>
        <ContactUsWidget defaultMode={ContactUsMode.FreeTrial} showAllModes={true} />
      </PageSection>
    </>
  );
};
UseCasesPage.displayName = 'UseCasesPage';
export default UseCasesPage;
